import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { 
  createOpportunity,
  createBuilder,
  createContact,
  createActivity,
  createQuote,
  createBuilding, 
  createFloor, 
  createDoor,
  createMultipleDoors,
  parseFloorRange,
  createDelivery,
  createSampleOpportunity,
  createSampleBuilders,
  createSampleQuote,
  cloneBuildings,
  calculateQuoteTotals,
  MOCK_PRODUCTS,
  AUTO_ASSIGN_RULES,
  generateId
} from '../data/mockData';

const STORAGE_KEY = 'door-scheduler-crm-data';

function normalizeContact(contact) {
  return createContact(contact || {});
}

function normalizeBuilder(builder) {
  const existingContacts = Array.isArray(builder?.contacts) ? builder.contacts : [];
  const fallbackContact = (builder?.contact || builder?.email || builder?.phone)
    ? [normalizeContact({
        name: builder.contact || '',
        email: builder.email || '',
        phone: builder.phone || '',
        role: ''
      })]
    : [];
  const normalizedContacts = existingContacts.length > 0
    ? existingContacts.map(normalizeContact)
    : fallbackContact;
  const primaryContact = normalizedContacts[0] || {};

  return {
    ...builder,
    contact: builder?.contact || primaryContact.name || '',
    email: builder?.email || primaryContact.email || '',
    phone: builder?.phone || primaryContact.phone || '',
    contacts: normalizedContacts,
    activities: Array.isArray(builder?.activities) ? builder.activities : [],
    address: builder?.address || '',
    builderType: builder?.builderType || '',
    customerStatus: builder?.customerStatus || '',
    accountStatus: builder?.accountStatus || '',
    creditLimit: builder?.creditLimit || '',
  };
}

function normalizeOpportunity(opportunity) {
  return {
    ...opportunity,
    industry: opportunity?.industry || '',
    projectDescription: opportunity?.projectDescription || '',
    tenderLinkId: opportunity?.tenderLinkId || '',
    projectTypes: Array.isArray(opportunity?.projectTypes) ? opportunity.projectTypes : [],
    status: opportunity?.status || 'qualification',
    phase: opportunity?.phase || '',
    jobChaseDate: opportunity?.jobChaseDate || null,
    jobChaseNotes: opportunity?.jobChaseNotes || '',
    expectedProjectStart: opportunity?.expectedProjectStart || null,
    expectedProjectFinish: opportunity?.expectedProjectFinish || null,
    expectedSupplyStart: opportunity?.expectedSupplyStart || null,
    builders: Array.isArray(opportunity?.builders) ? opportunity.builders.map(normalizeBuilder) : []
  };
}

// Initial state
const initialState = {
  // CRM
  opportunities: [],
  currentOpportunity: null,
  currentBuilder: null,
  
  // Scheduling
  quotes: [],
  currentQuote: null,
  selectedNode: null,
  importedProducts: [],
  
  // Delivery
  deliveries: [],
  deliverySelections: [],
  currentDelivery: null,
  
  // Navigation
  activeModule: 'crm', // 'crm' | 'scheduling' | 'deliveries'
};

// Action types
const ActionTypes = {
  LOAD_DATA: 'LOAD_DATA',
  SET_ACTIVE_MODULE: 'SET_ACTIVE_MODULE',
  
  // CRM Actions
  CREATE_OPPORTUNITY: 'CREATE_OPPORTUNITY',
  UPDATE_OPPORTUNITY: 'UPDATE_OPPORTUNITY',
  DELETE_OPPORTUNITY: 'DELETE_OPPORTUNITY',
  SET_CURRENT_OPPORTUNITY: 'SET_CURRENT_OPPORTUNITY',
  
  // Builder Actions
  ADD_BUILDER: 'ADD_BUILDER',
  UPDATE_BUILDER: 'UPDATE_BUILDER',
  DELETE_BUILDER: 'DELETE_BUILDER',
  SET_CURRENT_BUILDER: 'SET_CURRENT_BUILDER',
  ADD_ACTIVITY: 'ADD_ACTIVITY',
  MARK_BUILDER_WON: 'MARK_BUILDER_WON',
  
  // Quote Actions
  CREATE_QUOTE: 'CREATE_QUOTE',
  SET_CURRENT_QUOTE: 'SET_CURRENT_QUOTE',
  CONVERT_QUOTE_TO_SCHEDULE: 'CONVERT_QUOTE_TO_SCHEDULE',
  DUPLICATE_QUOTE: 'DUPLICATE_QUOTE',
  SET_QUOTE_ACTIVE: 'SET_QUOTE_ACTIVE',
  
  // Scheduling Actions
  SELECT_NODE: 'SELECT_NODE',
  ADD_BUILDING: 'ADD_BUILDING',
  ADD_FLOOR: 'ADD_FLOOR',
  ADD_DOOR: 'ADD_DOOR',
  ADD_MULTIPLE_DOORS: 'ADD_MULTIPLE_DOORS',
  ADD_MULTIPLE_FLOORS: 'ADD_MULTIPLE_FLOORS',
  UPDATE_DOOR: 'UPDATE_DOOR',
  ADD_PRODUCT_TO_DOOR: 'ADD_PRODUCT_TO_DOOR',
  REMOVE_PRODUCT_FROM_DOOR: 'REMOVE_PRODUCT_FROM_DOOR',
  AUTO_ASSIGN_HARDWARE: 'AUTO_ASSIGN_HARDWARE',
  AUTO_BUILD_SCHEDULE: 'AUTO_BUILD_SCHEDULE',
  DELETE_BUILDING: 'DELETE_BUILDING',
  DELETE_FLOOR: 'DELETE_FLOOR',
  DELETE_DOOR: 'DELETE_DOOR',
  IMPORT_SCHEDULE: 'IMPORT_SCHEDULE',
  
  // Delivery Actions
  TOGGLE_DELIVERY_SELECTION: 'TOGGLE_DELIVERY_SELECTION',
  CLEAR_DELIVERY_SELECTIONS: 'CLEAR_DELIVERY_SELECTIONS',
  CREATE_DELIVERY: 'CREATE_DELIVERY',
  UPDATE_DELIVERY_ERP_STAGE: 'UPDATE_DELIVERY_ERP_STAGE',
  SET_CURRENT_DELIVERY: 'SET_CURRENT_DELIVERY',
  
  // Demo
  LOAD_SAMPLE_DATA: 'LOAD_SAMPLE_DATA',
};

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.LOAD_DATA:
      return { ...state, ...action.payload };

    case ActionTypes.SET_ACTIVE_MODULE:
      return { ...state, activeModule: action.payload };

    // === CRM ACTIONS ===
    case ActionTypes.CREATE_OPPORTUNITY: {
      const newOpp = createOpportunity(action.payload);
      return { 
        ...state, 
        opportunities: [...state.opportunities, newOpp],
        currentOpportunity: newOpp
      };
    }

    case ActionTypes.UPDATE_OPPORTUNITY: {
      const { id, updates } = action.payload;
      const updatedOpps = state.opportunities.map(o => 
        o.id === id ? { ...o, ...updates } : o
      );
      const updatedCurrent = state.currentOpportunity?.id === id 
        ? { ...state.currentOpportunity, ...updates }
        : state.currentOpportunity;
      return { ...state, opportunities: updatedOpps, currentOpportunity: updatedCurrent };
    }

    case ActionTypes.DELETE_OPPORTUNITY: {
      return {
        ...state,
        opportunities: state.opportunities.filter(o => o.id !== action.payload),
        currentOpportunity: state.currentOpportunity?.id === action.payload ? null : state.currentOpportunity,
        quotes: state.quotes.filter(q => q.opportunityId !== action.payload)
      };
    }

    case ActionTypes.SET_CURRENT_OPPORTUNITY: {
      const opp = state.opportunities.find(o => o.id === action.payload);
      return { ...state, currentOpportunity: opp || null, currentBuilder: null };
    }

    // === BUILDER ACTIONS ===
    case ActionTypes.ADD_BUILDER: {
      const newBuilder = createBuilder(action.payload);
      const updatedOpps = state.opportunities.map(o => 
        o.id === action.payload.opportunityId
          ? { ...o, builders: [...o.builders, newBuilder] }
          : o
      );
      const updatedCurrent = state.currentOpportunity?.id === action.payload.opportunityId
        ? { ...state.currentOpportunity, builders: [...state.currentOpportunity.builders, newBuilder] }
        : state.currentOpportunity;
      return { ...state, opportunities: updatedOpps, currentOpportunity: updatedCurrent };
    }

    case ActionTypes.UPDATE_BUILDER: {
      const { opportunityId, builderId, updates } = action.payload;
      const updateBuilders = (builders) => 
        builders.map(b => b.id === builderId ? { ...b, ...updates } : b);
      
      const updatedOpps = state.opportunities.map(o => 
        o.id === opportunityId
          ? { ...o, builders: updateBuilders(o.builders) }
          : o
      );
      const updatedCurrent = state.currentOpportunity?.id === opportunityId
        ? { ...state.currentOpportunity, builders: updateBuilders(state.currentOpportunity.builders) }
        : state.currentOpportunity;
      const updatedBuilder = state.currentBuilder?.id === builderId
        ? { ...state.currentBuilder, ...updates }
        : state.currentBuilder;
      
      return { ...state, opportunities: updatedOpps, currentOpportunity: updatedCurrent, currentBuilder: updatedBuilder };
    }

    case ActionTypes.DELETE_BUILDER: {
      const { opportunityId, builderId } = action.payload;
      const removeBuilder = (builders) => builders.filter(b => b.id !== builderId);
      
      const updatedOpps = state.opportunities.map(o => 
        o.id === opportunityId
          ? { ...o, builders: removeBuilder(o.builders) }
          : o
      );
      const updatedCurrent = state.currentOpportunity?.id === opportunityId
        ? { ...state.currentOpportunity, builders: removeBuilder(state.currentOpportunity.builders) }
        : state.currentOpportunity;
      
      return { 
        ...state, 
        opportunities: updatedOpps, 
        currentOpportunity: updatedCurrent,
        currentBuilder: state.currentBuilder?.id === builderId ? null : state.currentBuilder,
        quotes: state.quotes.filter(q => q.builderId !== builderId)
      };
    }

    case ActionTypes.SET_CURRENT_BUILDER: {
      const builder = state.currentOpportunity?.builders.find(b => b.id === action.payload);
      return { ...state, currentBuilder: builder || null };
    }

    case ActionTypes.ADD_ACTIVITY: {
      const { opportunityId, builderId, activity } = action.payload;
      const newActivity = createActivity(activity);
      
      const addActivity = (builders) => 
        builders.map(b => b.id === builderId 
          ? { ...b, activities: [newActivity, ...b.activities] }
          : b
        );
      
      const updatedOpps = state.opportunities.map(o => 
        o.id === opportunityId
          ? { ...o, builders: addActivity(o.builders) }
          : o
      );
      const updatedCurrent = state.currentOpportunity?.id === opportunityId
        ? { ...state.currentOpportunity, builders: addActivity(state.currentOpportunity.builders) }
        : state.currentOpportunity;
      const updatedBuilder = state.currentBuilder?.id === builderId
        ? { ...state.currentBuilder, activities: [newActivity, ...state.currentBuilder.activities] }
        : state.currentBuilder;
      
      return { ...state, opportunities: updatedOpps, currentOpportunity: updatedCurrent, currentBuilder: updatedBuilder };
    }

    case ActionTypes.MARK_BUILDER_WON: {
      const { opportunityId, builderId } = action.payload;
      
      // Mark builder as won, others as lost
      const updateBuilders = (builders) => 
        builders.map(b => ({
          ...b,
          status: b.id === builderId ? 'won' : (b.status !== 'lost' ? 'lost' : b.status)
        }));
      
      // Update opportunity status
      const updatedOpps = state.opportunities.map(o => 
        o.id === opportunityId
          ? { ...o, builders: updateBuilders(o.builders), status: 'won' }
          : o
      );
      const updatedCurrent = state.currentOpportunity?.id === opportunityId
        ? { ...state.currentOpportunity, builders: updateBuilders(state.currentOpportunity.builders), status: 'won' }
        : state.currentOpportunity;
      
      // Convert quote to active schedule
      const updatedQuotes = state.quotes.map(q => 
        q.builderId === builderId ? { ...q, status: 'active' } : q
      );
      const updatedCurrentQuote = state.currentQuote?.builderId === builderId
        ? { ...state.currentQuote, status: 'active' }
        : state.currentQuote;
      
      return { 
        ...state, 
        opportunities: updatedOpps, 
        currentOpportunity: updatedCurrent,
        quotes: updatedQuotes,
        currentQuote: updatedCurrentQuote
      };
    }

    // === QUOTE ACTIONS ===
    case ActionTypes.CREATE_QUOTE: {
      const { opportunityId, builderId } = action.payload;
      const opportunity = state.opportunities.find(o => o.id === opportunityId);
      const builder = opportunity?.builders.find(b => b.id === builderId);
      
      const newQuote = createQuote({
        opportunityId,
        builderId,
        name: `${opportunity?.name || 'Quote'} - ${builder?.name || 'Builder'}`
      });
      
      // Link quote to builder
      const linkQuote = (builders) => 
        builders.map(b => b.id === builderId ? { ...b, quoteId: newQuote.id } : b);
      
      const updatedOpps = state.opportunities.map(o => 
        o.id === opportunityId
          ? { ...o, builders: linkQuote(o.builders) }
          : o
      );
      const updatedCurrent = state.currentOpportunity?.id === opportunityId
        ? { ...state.currentOpportunity, builders: linkQuote(state.currentOpportunity.builders) }
        : state.currentOpportunity;
      const updatedBuilder = state.currentBuilder?.id === builderId
        ? { ...state.currentBuilder, quoteId: newQuote.id }
        : state.currentBuilder;
      
      return { 
        ...state, 
        quotes: [...state.quotes, newQuote],
        currentQuote: newQuote,
        opportunities: updatedOpps,
        currentOpportunity: updatedCurrent,
        currentBuilder: updatedBuilder,
        activeModule: 'scheduling'
      };
    }

    case ActionTypes.SET_CURRENT_QUOTE: {
      const quote = state.quotes.find(q => q.id === action.payload);
      return { ...state, currentQuote: quote || null, selectedNode: null };
    }

    case ActionTypes.DUPLICATE_QUOTE: {
      if (!state.currentQuote) return state;
      const sourceQuote = state.currentQuote;
      
      // Find max version for this builder
      const builderQuotes = state.quotes.filter(q => q.builderId === sourceQuote.builderId);
      const maxVersion = Math.max(...builderQuotes.map(q => q.version || 1));
      const newVersion = maxVersion + 1;
      
      // Create duplicated quote
      const duplicatedQuote = createQuote({
        opportunityId: sourceQuote.opportunityId,
        builderId: sourceQuote.builderId,
        name: sourceQuote.name.replace(/v\d+$/, '').trim() + ` v${newVersion}`,
        version: newVersion,
        isActive: false, // New duplicates are not active by default
        buildings: cloneBuildings(sourceQuote.buildings)
      });
      duplicatedQuote.status = sourceQuote.status === 'active' ? 'active' : 'draft';
      
      return {
        ...state,
        quotes: [...state.quotes, duplicatedQuote],
        currentQuote: duplicatedQuote
      };
    }

    case ActionTypes.SET_QUOTE_ACTIVE: {
      const quoteId = action.payload;
      const targetQuote = state.quotes.find(q => q.id === quoteId);
      if (!targetQuote) return state;
      
      // Set this quote as active, deactivate other quotes for same builder
      const updatedQuotes = state.quotes.map(q => {
        if (q.builderId === targetQuote.builderId) {
          return { ...q, isActive: q.id === quoteId };
        }
        return q;
      });
      
      const updatedCurrentQuote = state.currentQuote?.id === quoteId
        ? { ...state.currentQuote, isActive: true }
        : state.currentQuote?.builderId === targetQuote.builderId
          ? { ...state.currentQuote, isActive: false }
          : state.currentQuote;
      
      return {
        ...state,
        quotes: updatedQuotes,
        currentQuote: updatedCurrentQuote
      };
    }

    // === SCHEDULING ACTIONS ===
    case ActionTypes.SELECT_NODE:
      return { ...state, selectedNode: action.payload };

    case ActionTypes.ADD_BUILDING: {
      if (!state.currentQuote) return state;
      const newBuilding = createBuilding(action.payload.name);
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: [...state.currentQuote.buildings, newBuilding]
      });
    }

    case ActionTypes.ADD_FLOOR: {
      if (!state.currentQuote) return state;
      const { buildingId, name } = action.payload;
      const newFloor = createFloor(name);
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b => 
          b.id === buildingId 
            ? { ...b, floors: [...b.floors, newFloor] }
            : b
        )
      });
    }

    case ActionTypes.ADD_MULTIPLE_FLOORS: {
      if (!state.currentQuote) return state;
      const { buildingId: mfBuildingId, floorNames } = action.payload;
      const newFloors = floorNames.map(n => createFloor(n));
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b =>
          b.id === mfBuildingId
            ? { ...b, floors: [...b.floors, ...newFloors] }
            : b
        )
      });
    }

    case ActionTypes.ADD_DOOR: {
      if (!state.currentQuote) return state;
      const { buildingId, floorId, name, type } = action.payload;
      const newDoor = createDoor(name, type);
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b => 
          b.id === buildingId 
            ? {
                ...b,
                floors: b.floors.map(f =>
                  f.id === floorId
                    ? { ...f, doors: [...f.doors, newDoor] }
                    : f
                )
              }
            : b
        )
      });
    }

    case ActionTypes.ADD_MULTIPLE_DOORS: {
      if (!state.currentQuote) return state;
      const { buildingId, floorId, prefix, count, type } = action.payload;
      const newDoors = createMultipleDoors(prefix, count, type);
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b => 
          b.id === buildingId 
            ? {
                ...b,
                floors: b.floors.map(f =>
                  f.id === floorId
                    ? { ...f, doors: [...f.doors, ...newDoors] }
                    : f
                )
              }
            : b
        )
      });
    }

    case ActionTypes.UPDATE_DOOR: {
      if (!state.currentQuote) return state;
      const { buildingId, floorId, doorId, updates } = action.payload;
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b => 
          b.id === buildingId 
            ? {
                ...b,
                floors: b.floors.map(f =>
                  f.id === floorId
                    ? {
                        ...f,
                        doors: f.doors.map(d =>
                          d.id === doorId ? { ...d, ...updates } : d
                        )
                      }
                    : f
                )
              }
            : b
        )
      });
    }

    case ActionTypes.ADD_PRODUCT_TO_DOOR: {
      if (!state.currentQuote) return state;
      const { buildingId, floorId, doorId, productId } = action.payload;
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b => 
          b.id === buildingId 
            ? {
                ...b,
                floors: b.floors.map(f =>
                  f.id === floorId
                    ? {
                        ...f,
                        doors: f.doors.map(d =>
                          d.id === doorId 
                            ? { ...d, products: [...d.products, productId] }
                            : d
                        )
                      }
                    : f
                )
              }
            : b
        )
      });
    }

    case ActionTypes.REMOVE_PRODUCT_FROM_DOOR: {
      if (!state.currentQuote) return state;
      const { buildingId, floorId, doorId, productId } = action.payload;
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b => 
          b.id === buildingId 
            ? {
                ...b,
                floors: b.floors.map(f =>
                  f.id === floorId
                    ? {
                        ...f,
                        doors: f.doors.map(d =>
                          d.id === doorId 
                            ? { ...d, products: d.products.filter(p => p !== productId) }
                            : d
                        )
                      }
                    : f
                )
              }
            : b
        )
      });
    }

    case ActionTypes.AUTO_ASSIGN_HARDWARE: {
      if (!state.currentQuote) return state;
      const { scope, buildingId, floorId } = action.payload;
      
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b => {
          if (scope === 'building' && b.id !== buildingId) return b;
          if (scope === 'floor' && b.id !== buildingId) return b;
          
          return {
            ...b,
            floors: b.floors.map(f => {
              if (scope === 'floor' && f.id !== floorId) return f;
              
              return {
                ...f,
                doors: f.doors.map(d => ({
                  ...d,
                  products: AUTO_ASSIGN_RULES[d.type] || []
                }))
              };
            })
          };
        })
      });
    }

    case ActionTypes.AUTO_BUILD_SCHEDULE: {
      if (!state.currentQuote) return state;
      
      // Auto-generate a complete schedule structure
      const buildingNames = ['Tower A', 'Tower B', 'Podium'];
      const floorTypes = [
        { name: 'Ground Floor', prefix: 'GF', doorCount: 8, types: ['exit', 'exit', 'standard', 'standard', 'standard', 'fire-rated', 'standard', 'standard'] },
        { name: 'Level 1', prefix: 'L1', doorCount: 12, types: ['standard', 'standard', 'standard', 'standard', 'exit', 'standard', 'standard', 'standard', 'standard', 'fire-rated', 'standard', 'standard'] },
        { name: 'Level 2', prefix: 'L2', doorCount: 12, types: ['standard', 'standard', 'standard', 'standard', 'exit', 'standard', 'standard', 'standard', 'standard', 'fire-rated', 'standard', 'standard'] }
      ];
      
      const buildings = buildingNames.slice(0, 2).map(bName => ({
        id: generateId(),
        name: bName,
        floors: floorTypes.map(ft => ({
          id: generateId(),
          name: ft.name,
          doors: ft.types.map((type, idx) => ({
            id: generateId(),
            name: `${ft.prefix}-${String(idx + 1).padStart(3, '0')}`,
            type: type,
            products: AUTO_ASSIGN_RULES[type] || [],
            attributes: { width: type === 'exit' ? 900 : 800, height: 2100 }
          }))
        }))
      }));
      
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings
      });
    }

    case ActionTypes.DELETE_BUILDING: {
      if (!state.currentQuote) return state;
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.filter(b => b.id !== action.payload)
      }, true);
    }

    case ActionTypes.DELETE_FLOOR: {
      if (!state.currentQuote) return state;
      const { buildingId, floorId } = action.payload;
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b =>
          b.id === buildingId
            ? { ...b, floors: b.floors.filter(f => f.id !== floorId) }
            : b
        )
      }, true);
    }

    case ActionTypes.DELETE_DOOR: {
      if (!state.currentQuote) return state;
      const { buildingId, floorId, doorId } = action.payload;
      return updateQuoteInState(state, {
        ...state.currentQuote,
        buildings: state.currentQuote.buildings.map(b =>
          b.id === buildingId
            ? {
                ...b,
                floors: b.floors.map(f =>
                  f.id === floorId
                    ? { ...f, doors: f.doors.filter(d => d.id !== doorId) }
                    : f
                )
              }
            : b
        )
      }, true);
    }

    // === IMPORT SCHEDULE ===
    case ActionTypes.IMPORT_SCHEDULE: {
      if (!state.currentQuote) return state;
      const { buildings, newProducts, appendMode } = action.payload;
      const updatedBuildings = appendMode
        ? [...(state.currentQuote.buildings || []), ...buildings]
        : buildings;
      const updatedQuote = { ...state.currentQuote, buildings: updatedBuildings };
      return {
        ...updateQuoteInState(state, updatedQuote, true),
        importedProducts: [...(state.importedProducts || []), ...newProducts],
      };
    }

    // === DELIVERY ACTIONS ===
    case ActionTypes.TOGGLE_DELIVERY_SELECTION: {
      const { type, id, path } = action.payload;
      const exists = state.deliverySelections.find(s => s.id === id && s.type === type);
      if (exists) {
        return {
          ...state,
          deliverySelections: state.deliverySelections.filter(s => !(s.id === id && s.type === type))
        };
      }
      return {
        ...state,
        deliverySelections: [...state.deliverySelections, { type, id, path }]
      };
    }

    case ActionTypes.CLEAR_DELIVERY_SELECTIONS:
      return { ...state, deliverySelections: [] };

    case ActionTypes.CREATE_DELIVERY: {
      const newDelivery = createDelivery(action.payload);
      return { 
        ...state, 
        deliveries: [...state.deliveries, newDelivery],
        currentDelivery: newDelivery,
        deliverySelections: []
      };
    }

    case ActionTypes.UPDATE_DELIVERY_ERP_STAGE: {
      const { deliveryId, stage } = action.payload;
      const updatedDeliveries = state.deliveries.map(d => {
        if (d.id !== deliveryId) return d;
        
        const updatedStages = { ...d.erpStages };
        updatedStages[stage] = { completed: true, date: new Date().toISOString() };
        
        // Also mark previous stages as complete
        const stageOrder = ['allocated', 'picking', 'packing', 'shipped', 'invoiced'];
        const stageIndex = stageOrder.indexOf(stage);
        stageOrder.slice(0, stageIndex).forEach(s => {
          if (!updatedStages[s].completed) {
            updatedStages[s] = { completed: true, date: new Date().toISOString() };
          }
        });
        
        return { ...d, erpStages: updatedStages, status: stage };
      });
      
      const updatedCurrent = state.currentDelivery?.id === deliveryId
        ? updatedDeliveries.find(d => d.id === deliveryId)
        : state.currentDelivery;
      
      return { ...state, deliveries: updatedDeliveries, currentDelivery: updatedCurrent };
    }

    case ActionTypes.SET_CURRENT_DELIVERY: {
      const delivery = state.deliveries.find(d => d.id === action.payload);
      return { ...state, currentDelivery: delivery || null };
    }

    // === DEMO ===
    case ActionTypes.LOAD_SAMPLE_DATA: {
      const opportunity = createSampleOpportunity();
      const builders = createSampleBuilders(opportunity.id);
      opportunity.builders = builders;
      
      // Create quote for first builder
      const quote = createSampleQuote(opportunity.id, builders[0].id);
      builders[0].quoteId = quote.id;
      
      return {
        ...state,
        opportunities: [opportunity],
        currentOpportunity: opportunity,
        quotes: [quote],
        activeModule: 'crm'
      };
    }

    default:
      return state;
  }
}

// Helper to update quote in state
function updateQuoteInState(state, updatedQuote, clearSelection = false) {
  const updatedQuotes = state.quotes.map(q => 
    q.id === updatedQuote.id ? updatedQuote : q
  );
  return { 
    ...state, 
    quotes: updatedQuotes, 
    currentQuote: updatedQuote,
    selectedNode: clearSelection ? null : state.selectedNode
  };
}

// Context
const AppContext = createContext(null);

// Provider component
export function AppProvider({ children }) {
  // Initialize state from localStorage
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        const opportunities = Array.isArray(data.opportunities)
          ? data.opportunities.map(normalizeOpportunity)
          : [];
        const currentOpportunity = data.currentOpportunity
          ? normalizeOpportunity(data.currentOpportunity)
          : null;
        const currentBuilder = currentOpportunity?.builders.find(b => b.id === data.currentBuilder?.id) || null;

        return {
          ...initialState,
          ...data,
          opportunities,
          currentOpportunity,
          currentBuilder,
          importedProducts: Array.isArray(data.importedProducts) ? data.importedProducts : [],
        };
      }
    } catch (e) {
      console.error('Failed to load saved data:', e);
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(appReducer, null, getInitialState);

  // Save to localStorage on state change
  useEffect(() => {
    const dataToSave = {
      opportunities: state.opportunities,
      quotes: state.quotes,
      deliveries: state.deliveries,
      currentOpportunity: state.currentOpportunity,
      currentQuote: state.currentQuote,
      activeModule: state.activeModule,
      importedProducts: state.importedProducts,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  }, [state.opportunities, state.quotes, state.deliveries, state.currentOpportunity, state.currentQuote, state.activeModule, state.importedProducts]);

  // Actions
  const actions = {
    setActiveModule: (module) => dispatch({ type: ActionTypes.SET_ACTIVE_MODULE, payload: module }),
    
    // CRM
    createOpportunity: (data) => dispatch({ type: ActionTypes.CREATE_OPPORTUNITY, payload: data }),
    updateOpportunity: (id, updates) => dispatch({ type: ActionTypes.UPDATE_OPPORTUNITY, payload: { id, updates } }),
    deleteOpportunity: (id) => dispatch({ type: ActionTypes.DELETE_OPPORTUNITY, payload: id }),
    setCurrentOpportunity: (id) => dispatch({ type: ActionTypes.SET_CURRENT_OPPORTUNITY, payload: id }),
    
    // Builders
    addBuilder: (opportunityId, data) => dispatch({ type: ActionTypes.ADD_BUILDER, payload: { opportunityId, ...data } }),
    updateBuilder: (opportunityId, builderId, updates) => dispatch({ type: ActionTypes.UPDATE_BUILDER, payload: { opportunityId, builderId, updates } }),
    deleteBuilder: (opportunityId, builderId) => dispatch({ type: ActionTypes.DELETE_BUILDER, payload: { opportunityId, builderId } }),
    setCurrentBuilder: (id) => dispatch({ type: ActionTypes.SET_CURRENT_BUILDER, payload: id }),
    addActivity: (opportunityId, builderId, activity) => dispatch({ type: ActionTypes.ADD_ACTIVITY, payload: { opportunityId, builderId, activity } }),
    markBuilderWon: (opportunityId, builderId) => dispatch({ type: ActionTypes.MARK_BUILDER_WON, payload: { opportunityId, builderId } }),
    
    // Quotes
    createQuote: (opportunityId, builderId) => dispatch({ type: ActionTypes.CREATE_QUOTE, payload: { opportunityId, builderId } }),
    setCurrentQuote: (id) => dispatch({ type: ActionTypes.SET_CURRENT_QUOTE, payload: id }),
    duplicateQuote: () => dispatch({ type: ActionTypes.DUPLICATE_QUOTE }),
    setQuoteActive: (quoteId) => dispatch({ type: ActionTypes.SET_QUOTE_ACTIVE, payload: quoteId }),
    
    // Scheduling
    selectNode: (node) => dispatch({ type: ActionTypes.SELECT_NODE, payload: node }),
    addBuilding: (name) => dispatch({ type: ActionTypes.ADD_BUILDING, payload: { name } }),
    addFloor: (buildingId, name) => dispatch({ type: ActionTypes.ADD_FLOOR, payload: { buildingId, name } }),
    addMultipleFloors: (buildingId, rangeOrList) => {
      const floorNames = typeof rangeOrList === 'string' ? parseFloorRange(rangeOrList) : rangeOrList;
      dispatch({ type: ActionTypes.ADD_MULTIPLE_FLOORS, payload: { buildingId, floorNames } });
    },
    addDoor: (buildingId, floorId, name, type) => dispatch({ type: ActionTypes.ADD_DOOR, payload: { buildingId, floorId, name, type } }),
    addMultipleDoors: (buildingId, floorId, prefix, count, type) => dispatch({ type: ActionTypes.ADD_MULTIPLE_DOORS, payload: { buildingId, floorId, prefix, count, type } }),
    updateDoor: (buildingId, floorId, doorId, updates) => dispatch({ type: ActionTypes.UPDATE_DOOR, payload: { buildingId, floorId, doorId, updates } }),
    addProductToDoor: (buildingId, floorId, doorId, productId) => dispatch({ type: ActionTypes.ADD_PRODUCT_TO_DOOR, payload: { buildingId, floorId, doorId, productId } }),
    removeProductFromDoor: (buildingId, floorId, doorId, productId) => dispatch({ type: ActionTypes.REMOVE_PRODUCT_FROM_DOOR, payload: { buildingId, floorId, doorId, productId } }),
    autoAssignHardware: (scope, buildingId, floorId) => dispatch({ type: ActionTypes.AUTO_ASSIGN_HARDWARE, payload: { scope, buildingId, floorId } }),
    autoBuildSchedule: () => dispatch({ type: ActionTypes.AUTO_BUILD_SCHEDULE }),
    deleteBuilding: (buildingId) => dispatch({ type: ActionTypes.DELETE_BUILDING, payload: buildingId }),
    deleteFloor: (buildingId, floorId) => dispatch({ type: ActionTypes.DELETE_FLOOR, payload: { buildingId, floorId } }),
    deleteDoor: (buildingId, floorId, doorId) => dispatch({ type: ActionTypes.DELETE_DOOR, payload: { buildingId, floorId, doorId } }),
    
    // Delivery
    toggleDeliverySelection: (type, id, path) => dispatch({ type: ActionTypes.TOGGLE_DELIVERY_SELECTION, payload: { type, id, path } }),
    clearDeliverySelections: () => dispatch({ type: ActionTypes.CLEAR_DELIVERY_SELECTIONS }),
    createDelivery: (data) => dispatch({ type: ActionTypes.CREATE_DELIVERY, payload: data }),
    updateDeliveryErpStage: (deliveryId, stage) => dispatch({ type: ActionTypes.UPDATE_DELIVERY_ERP_STAGE, payload: { deliveryId, stage } }),
    setCurrentDelivery: (id) => dispatch({ type: ActionTypes.SET_CURRENT_DELIVERY, payload: id }),
    
    // Demo
    loadSampleData: () => dispatch({ type: ActionTypes.LOAD_SAMPLE_DATA }),

    // Import
    importSchedule: (payload) => dispatch({ type: ActionTypes.IMPORT_SCHEDULE, payload }),
  };

  return (
    <AppContext.Provider value={{ state, actions, products: [...MOCK_PRODUCTS, ...(state.importedProducts || [])] }}>
      {children}
    </AppContext.Provider>
  );
}

// Hook
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
